import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Iconify from "../iconify/Iconify";
import { useGetMembersQuery } from "../../features/members/membersApiSlice";
import {
  getDefaultCheckedMemberTableColumns,
  memberTableColumns,
} from "../../utils/memberUtils";
import { createFullDateString, getGradCollection } from "../../utils/appUtils";
import { makeSiteOverviewPdf } from "../../utils/siteOverviewPdf";

const AGE_GROUPS = [
  { label: "Bambini", rangeLabel: "0-5", min: 0, max: 5 },
  { label: "Kinder", rangeLabel: "6-11", min: 6, max: 11 },
  { label: "Schüler", rangeLabel: "12-14", min: 12, max: 14 },
  { label: "Jugend", rangeLabel: "12-17", min: 12, max: 17 },
  { label: "Erwachsen", rangeLabel: "18-49", min: 18, max: 49 },
  { label: "Senioren", rangeLabel: "50-99+", min: 50, max: Infinity },
];

const calculateAge = (birthdate) => {
  if (!birthdate) return null;
  const date = new Date(birthdate);
  if (Number.isNaN(date.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - date.getFullYear();
  const monthDiff = today.getMonth() - date.getMonth();
  const dayDiff = today.getDate() - date.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age -= 1;
  }

  return age;
};

const getMonthLabel = (date = new Date()) =>
  date.toLocaleDateString("de-DE", { month: "long", year: "numeric" });

const gradCollection = getGradCollection();
const PREVIEW_LIMIT = 12;

const formatMemberPreviewValue = ({ member, column, siteName }) => {
  const value = member?.[column.value];
  if (column.data === "contract") {
    const contractValue = member?.contractRef?.[column.value];
    return contractValue == null ? "" : `${contractValue}`;
  }

  switch (column.value) {
    case "degree":
    case "belt": {
      const degreeValue = Number(member?.degree);
      if (!Number.isFinite(degreeValue) || degreeValue < 0) return "";
      const grad = gradCollection[degreeValue];
      return grad?.title || "";
    }
    case "birthdate":
    case "contractDate":
    case "beginningDate":
    case "lastTesting":
    case "createdAt":
      return value ? createFullDateString(new Date(value)) : "";
    case "standortRef":
      return siteName || member?.standortRef?.siteName || "";
    case "age": {
      const directAge = Number(value);
      if (Number.isFinite(directAge)) return `${directAge}`;
      const calculatedAge = calculateAge(member?.birthdate);
      return Number.isFinite(calculatedAge) ? `${calculatedAge}` : "";
    }
    default:
      return value == null ? "" : `${value}`;
  }
};

const getMemberSortValue = (member, column) => {
  if (!column) return "";
  if (column.data === "contract") {
    return member?.contractRef?.[column.value] ?? "";
  }

  switch (column.value) {
    case "standortRef":
      return member?.standortRef?.siteName || "";
    case "degree":
    case "belt":
      return member?.degree;
    case "age": {
      const directAge = Number(member?.age);
      if (Number.isFinite(directAge)) return directAge;
      return calculateAge(member?.birthdate);
    }
    default:
      return member?.[column.value];
  }
};

const toComparableValue = (value, column) => {
  if (value == null || value === "") {
    return { empty: true, type: "empty", value: "" };
  }

  const isDateColumn =
    column?.type === "date" ||
    [
      "birthdate",
      "contractDate",
      "beginningDate",
      "lastTesting",
      "createdAt",
    ].includes(column?.value);

  if (isDateColumn) {
    const parsedDate = new Date(value);
    if (!Number.isNaN(parsedDate.getTime())) {
      return { empty: false, type: "number", value: parsedDate.getTime() };
    }
  }

  const isNumberColumn =
    column?.type === "number" ||
    ["degree", "belt", "age", "contribution"].includes(column?.value);

  if (isNumberColumn) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return { empty: false, type: "number", value: numeric };
    }
  }

  return {
    empty: false,
    type: "text",
    value: String(value).trim().toLocaleLowerCase("de-DE"),
  };
};

export default function SiteOverviewDialog({ dialog, handleClose }) {
  const site = dialog?.data;
  const { data: members = [], isLoading } = useGetMembersQuery();
  const [selectedLabels, setSelectedLabels] = useState(
    getDefaultCheckedMemberTableColumns(memberTableColumns)
  );
  const [sortBy, setSortBy] = useState(
    memberTableColumns.find((column) => column.value === "lastName")?.value ||
      memberTableColumns?.[0]?.value ||
      ""
  );
  const [sortDirection, setSortDirection] = useState("asc");

  const selectedColumns = useMemo(
    () =>
      memberTableColumns.filter((col) => selectedLabels.includes(col.label)),
    [selectedLabels]
  );

  const siteMembers = useMemo(() => {
    if (!site?._id) return [];
    return (members || []).filter((member) => {
      const siteId = String(member?.standortRef?._id || member?.standortRef || "");
      return siteId === String(site._id);
    });
  }, [members, site?._id]);

  const activeMembers = useMemo(
    () =>
      siteMembers.filter(
        (member) => (member?.status || "").toLowerCase() === "active"
      ),
    [siteMembers]
  );

  const stats = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const newMembersThisMonth = siteMembers.filter((member) => {
      const created = member?.createdAt ? new Date(member.createdAt) : null;
      if (!created || Number.isNaN(created.getTime())) return false;
      return (
        created.getMonth() === currentMonth &&
        created.getFullYear() === currentYear
      );
    }).length;

    const terminationsThisMonth = siteMembers.filter((member) => {
      const termination = member?.terminationDate
        ? new Date(member.terminationDate)
        : null;
      if (!termination || Number.isNaN(termination.getTime())) return false;
      return (
        termination.getMonth() === currentMonth &&
        termination.getFullYear() === currentYear
      );
    }).length;

    return {
      totalActive: activeMembers.length,
      newMembersThisMonth,
      terminationsThisMonth,
      monthLabel: getMonthLabel(now),
    };
  }, [siteMembers, activeMembers.length]);

  const ageGroups = useMemo(() => {
    const counts = new Map(AGE_GROUPS.map((group) => [group.label, 0]));

    activeMembers.forEach((member) => {
      const age = calculateAge(member?.birthdate);
      if (age === null || age < 0) return;

      const matchingGroup = AGE_GROUPS.find(
        (group) => age >= group.min && age <= group.max
      );

      if (matchingGroup) {
        counts.set(
          matchingGroup.label,
          (counts.get(matchingGroup.label) || 0) + 1
        );
      }
    });

    return AGE_GROUPS.map((group) => ({
      ...group,
      value: counts.get(group.label) || 0,
    }));
  }, [activeMembers]);

  const sortColumn = useMemo(
    () =>
      memberTableColumns.find((column) => column.value === sortBy) ||
      memberTableColumns?.[0],
    [sortBy]
  );

  const sortedActiveMembers = useMemo(() => {
    const directionFactor = sortDirection === "asc" ? 1 : -1;
    const sortedMembers = [...activeMembers];

    sortedMembers.sort((leftMember, rightMember) => {
      const leftComparable = toComparableValue(
        getMemberSortValue(leftMember, sortColumn),
        sortColumn
      );
      const rightComparable = toComparableValue(
        getMemberSortValue(rightMember, sortColumn),
        sortColumn
      );

      if (leftComparable.empty && rightComparable.empty) return 0;
      if (leftComparable.empty) return 1;
      if (rightComparable.empty) return -1;

      let comparison = 0;

      if (
        leftComparable.type === "number" &&
        rightComparable.type === "number"
      ) {
        comparison = leftComparable.value - rightComparable.value;
      } else {
        comparison = String(leftComparable.value).localeCompare(
          String(rightComparable.value),
          "de",
          {
            numeric: true,
            sensitivity: "base",
          }
        );
      }

      if (comparison !== 0) return comparison * directionFactor;

      return `${leftMember?.lastName || ""} ${
        leftMember?.firstName || ""
      }`.localeCompare(
        `${rightMember?.lastName || ""} ${rightMember?.firstName || ""}`,
        "de",
        { numeric: true, sensitivity: "base" }
      );
    });

    return sortedMembers;
  }, [activeMembers, sortColumn, sortDirection]);

  const previewMembers = useMemo(
    () => sortedActiveMembers.slice(0, PREVIEW_LIMIT),
    [sortedActiveMembers]
  );

  const handleToggle = (label) => {
    setSelectedLabels((prev) =>
      prev.includes(label)
        ? prev.filter((item) => item !== label)
        : [...prev, label]
    );
  };

  const handleSelectAll = () => {
    setSelectedLabels(memberTableColumns.map((col) => col.label));
  };

  const handleReset = () => {
    setSelectedLabels(getDefaultCheckedMemberTableColumns(memberTableColumns));
  };

  const handleSelectNone = () => {
    setSelectedLabels([]);
  };

  const handleChangeSortDirection = (_event, value) => {
    if (value) {
      setSortDirection(value);
    }
  };

  const handleGeneratePdf = () => {
    makeSiteOverviewPdf({
      members: sortedActiveMembers,
      siteName: site?.siteName || "",
      selectedColumns,
      stats,
      ageGroups,
    });
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Iconify icon="mdi:file-pdf-box" width={26} />
            <Box>
              <Typography variant="h6">Standortübersicht</Typography>
              <Typography variant="body2" color="text.secondary">
                PDF gestalten, sortieren und vorab prüfen.
              </Typography>
            </Box>
          </Stack>
          <Button onClick={handleClose} color="inherit">
            <Iconify icon="icon-park-solid:close-one" width={24} />
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            borderColor: "rgba(2, 136, 209, 0.3)",
            background:
              "linear-gradient(140deg, rgba(2,136,209,0.08) 0%, rgba(56,142,60,0.05) 100%)",
          }}
        >
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            {site?.siteName || "Standort"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Konfigurieren Sie Felder, Sortierung und Vorschau für die
            Mitgliederliste.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
            <Chip
              size="small"
              color="primary"
              label={`Aktive Mitglieder: ${stats.totalActive}`}
            />
            <Chip
              size="small"
              label={`Neue Mitglieder (${stats.monthLabel}): ${stats.newMembersThisMonth}`}
            />
            <Chip
              size="small"
              label={`Kündigungen (${stats.monthLabel}): ${stats.terminationsThisMonth}`}
            />
          </Stack>
        </Paper>

        <Grid container spacing={2.5}>
          <Grid item xs={12} md={5}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle1">1. Felder auswählen</Typography>
                <Chip
                  size="small"
                  label={`${selectedColumns.length}/${memberTableColumns.length}`}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Diese Felder werden in Vorschau und PDF angezeigt.
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
                <Button size="small" color="inherit" onClick={handleSelectAll}>
                  Alle
                </Button>
                <Button size="small" color="inherit" onClick={handleReset}>
                  Standard
                </Button>
                <Button size="small" color="inherit" onClick={handleSelectNone}>
                  Keine
                </Button>
              </Stack>
              <Divider sx={{ mb: 1.5 }} />
              <FormGroup sx={{ maxHeight: 320, overflowY: "auto", pr: 1 }}>
                {memberTableColumns.map((col) => (
                  <FormControlLabel
                    key={col.label}
                    control={
                      <Checkbox
                        checked={selectedLabels.includes(col.label)}
                        onChange={() => handleToggle(col.label)}
                      />
                    }
                    label={col.label}
                  />
                ))}
              </FormGroup>
              {!selectedColumns.length && (
                <Typography variant="caption" color="error.main">
                  Bitte mindestens ein Feld auswählen.
                </Typography>
              )}
            </Paper>
          </Grid>
          <Grid item xs={12} md={7}>
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  2. Sortierung festlegen
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={7}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="site-overview-sort-label">
                        Sortierfeld
                      </InputLabel>
                      <Select
                        labelId="site-overview-sort-label"
                        value={sortBy}
                        label="Sortierfeld"
                        onChange={(event) => setSortBy(event.target.value)}
                      >
                        {memberTableColumns.map((column) => (
                          <MenuItem key={column.value} value={column.value}>
                            {column.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={5}>
                    <ToggleButtonGroup
                      exclusive
                      fullWidth
                      size="small"
                      value={sortDirection}
                      onChange={handleChangeSortDirection}
                    >
                      <ToggleButton value="asc">Aufsteigend</ToggleButton>
                      <ToggleButton value="desc">Absteigend</ToggleButton>
                    </ToggleButtonGroup>
                  </Grid>
                </Grid>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Leere Werte werden in der Liste automatisch nach unten gesetzt.
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle1">3. Vorschau</Typography>
                  <Chip
                    size="small"
                    label={`${sortedActiveMembers.length} aktive Mitglieder`}
                  />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Sortiert nach {sortColumn?.label || "-"} (
                  {sortDirection === "asc" ? "aufsteigend" : "absteigend"}).
                </Typography>
                {!selectedColumns.length ? (
                  <Typography variant="body2" color="text.secondary">
                    Vorschau verfügbar, sobald mindestens ein Feld ausgewählt
                    ist.
                  </Typography>
                ) : (
                  <>
                    <TableContainer
                      sx={{
                        maxHeight: 320,
                        borderRadius: 1,
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {selectedColumns.map((column) => (
                              <TableCell key={column.label}>
                                {column.label}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {previewMembers.map((member, index) => {
                            const rowKey =
                              member?._id ||
                              member?.memberNumber ||
                              `${member?.lastName || "mitglied"}-${index}`;

                            return (
                              <TableRow hover key={rowKey}>
                                {selectedColumns.map((column) => {
                                const displayValue = formatMemberPreviewValue({
                                  member,
                                  column,
                                  siteName: site?.siteName || "",
                                });

                                return (
                                  <TableCell key={`${rowKey}-${column.label}`}>
                                    {displayValue || "—"}
                                  </TableCell>
                                );
                                })}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {!!sortedActiveMembers.length && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block", mt: 1 }}
                      >
                        Zeigt {previewMembers.length} von{" "}
                        {sortedActiveMembers.length} aktiven Mitgliedern.
                      </Typography>
                    )}
                    {!sortedActiveMembers.length && (
                      <Typography variant="body2" color="text.secondary">
                        Für diesen Standort sind keine aktiven Mitglieder
                        vorhanden.
                      </Typography>
                    )}
                  </>
                )}
              </Paper>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Altersgruppen (aktive Mitglieder)
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {ageGroups.map((group) => (
                  <Chip
                    key={group.label}
                    size="small"
                    variant="outlined"
                    label={`${group.label} (${group.rangeLabel}): ${group.value}`}
                  />
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>
        {isLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Lade Mitglieder ...
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Typography variant="body2" color="text.secondary">
          PDF umfasst {selectedColumns.length} Felder und{" "}
          {sortedActiveMembers.length} Mitglieder.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button onClick={handleClose} color="inherit">
            Abbrechen
          </Button>
          <Button onClick={handleReset} color="inherit">
            Standardfelder
          </Button>
          <Button
            onClick={handleGeneratePdf}
            variant="contained"
            color="secondary"
            disabled={
              isLoading || !sortedActiveMembers.length || !selectedColumns.length
            }
          >
            PDF erstellen
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

SiteOverviewDialog.propTypes = {
  dialog: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
};
